import { api } from '../../../../../config';
import { getAuthToken } from '../../../../../auth/tokenvalidation';
import { formatNumber } from '../../../../../utils/numberformatter';
import {
  pick,
  unwrapListItems,
} from '../../../../../Components/QuantityTable/utils/quantityDeliveryUtils';

export const CARGO_RATIO_LABELS = [
  '50% : 50%',
  '75% : 25%',
  '25% : 75%',
  '100% : 0%',
  '0% : 100%',
];

const pickNamedValue = value => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'object') {
    return (
      value.name ??
      value.Name ??
      value.location?.name ??
      value.location?.Name ??
      value.Location?.name ??
      value.Location?.Name ??
      value.ratioName ??
      value.RatioName ??
      null
    );
  }
  return value;
};

const unwrapQuantityInformations = deliveryItem => {
  if (!deliveryItem) return [];

  const nested =
    deliveryItem?.quantityInformations ??
    deliveryItem?.QuantityInformations ??
    [];

  return Array.isArray(nested) ? nested : [];
};

export const fetchQuantityInformationTargets = async (
  procurementId,
  quantityDeliveryTermId
) => {
  const token = getAuthToken();
  if (!token || !procurementId || !quantityDeliveryTermId) return [];

  const response = await fetch(
    `${api.API_URL}/quantity-delivery?ProcurementId=${procurementId}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to load quantity delivery (${response.status})`);
  }

  const result = await response.json();
  const deliveryItems = unwrapListItems(result.data ?? result);

  const matchedDeliveryItem = deliveryItems.find(
    item => String(pick(item, 'id', 'Id')) === String(quantityDeliveryTermId)
  );

  const quantityInformations = matchedDeliveryItem
    ? unwrapQuantityInformations(matchedDeliveryItem)
    : deliveryItems.flatMap(unwrapQuantityInformations).filter(item => {
        const termId = pick(
          item,
          'quantityDeliveryTermId',
          'QuantityDeliveryTermId'
        );
        return String(termId) === String(quantityDeliveryTermId);
      });

  return quantityInformations
    .map(item => ({
      quantityInformationFk: pick(item, 'id', 'Id'),
      quantity: pick(item, 'quantity', 'Quantity'),
      cargoRatios: (pick(item, 'cargoRatios', 'CargoRatios') || []).map(
        ratio => ({
          cargoRatio: pick(ratio, 'cargoRatio', 'CargoRatio'),
          alphaHps:
            pick(ratio, 'alphaHps', 'AlphaHps') ??
            pick(ratio, 'hpsAlpha', 'HpsAlpha') ??
            null,
        })
      ),
    }))
    .filter(item => item.quantityInformationFk)
    .sort((a, b) => Number(a.quantity) - Number(b.quantity));
};

export const fetchCargoRatioOptions = async () => {
  const token = getAuthToken();
  if (!token) return [];

  try {
    const response = await fetch(
      `${api.API_URL}/ratio-lpg?PageNumber=1&PageSize=100`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) return [];

    const result = await response.json();
    const items = unwrapListItems(result.data ?? result);

    return items
      .map(item => ({
        id: pick(item, 'id', 'Id'),
        label:
          pick(item, 'ratioName', 'RatioName') ??
          pickNamedValue(pick(item, 'ratioLpg', 'RatioLpg')),
      }))
      .filter(option => option.id && option.label)
      .filter(option => CARGO_RATIO_LABELS.includes(option.label));
  } catch {
    return [];
  }
};

export const fetchIncotermAlphaMap = async quantityDeliveryTermId => {
  const token = getAuthToken();
  if (!token || !quantityDeliveryTermId) return new Map();

  try {
    const response = await fetch(
      `${api.API_URL}/quantity-incoterm?QuantityDeliveryTermId=${quantityDeliveryTermId}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) return new Map();

    const result = await response.json();
    const items = unwrapListItems(result.data ?? result);

    const alphaMap = new Map();
    items.forEach(item => {
      const list = pick(item, 'cargoRatios', 'CargoRatios') || [];
      (Array.isArray(list) ? list : []).forEach(cr => {
        const incotermFk = pick(cr, 'quantityIncotermFK', 'QuantityIncotermFK');
        const qiFk = pick(cr, 'quantityInformationFK', 'QuantityInformationFK');
        const crVal = pick(cr, 'cargoRatio', 'CargoRatio');
        const alpha = pick(cr, 'alphaHps', 'AlphaHps') ?? null;

        if (
          incotermFk !== null &&
          incotermFk !== undefined &&
          qiFk !== null &&
          qiFk !== undefined &&
          crVal !== null &&
          crVal !== undefined
        ) {
          alphaMap.set(
            `${String(incotermFk)}-${String(qiFk)}-${getCargoRatioKey(crVal)}`,
            alpha
          );
        }
      });
    });

    return alphaMap;
  } catch {
    return new Map();
  }
};

export const getCargoRatioKey = cargoRatioId => {
  const numericValue = Number(cargoRatioId);
  const resolved = Number.isFinite(numericValue) ? numericValue : cargoRatioId;
  return String(resolved);
};

const resolveCargoRatioValue = cargoRatioId => {
  const numericValue = Number(cargoRatioId);
  return Number.isFinite(numericValue) ? numericValue : cargoRatioId;
};

// Kunci baris cargo ratio: parcel x incoterm x ratio. Tanpa incoterm, dua incoterm yang
// berbagi parcel akan dianggap baris yang sama - itulah bug alpha bocor lintas incoterm.
const getSelectionKey = (
  quantityInformationFk,
  quantityIncotermFk,
  cargoRatio
) => `${quantityInformationFk}-${quantityIncotermFk ?? ''}-${cargoRatio}`;

const getSelectionIncotermFk = selection =>
  pick(selection, 'quantityIncotermFK', 'QuantityIncotermFK');

export const collectIncotermIdsFromBases = (incotermBases = []) => [
  ...new Set(
    incotermBases
      .map(base => base?.quantityIncotermId)
      .filter(id => id !== null && id !== undefined && id !== '')
      .map(String)
  ),
];

export const fetchAllCargoRatioSelections = async (
  quantityInformationFks = null
) => {
  const token = getAuthToken();
  if (!token) return [];

  const fks = Array.isArray(quantityInformationFks)
    ? [
        ...new Set(
          quantityInformationFks
            .filter(fk => fk !== null && fk !== undefined && fk !== '')
            .map(String)
        ),
      ]
    : quantityInformationFks !== null &&
        quantityInformationFks !== undefined &&
        quantityInformationFks !== ''
      ? [String(quantityInformationFks)]
      : [];

  const fetchByQuery = async query => {
    const response = await fetch(
      `${api.API_URL}/quantity-information-cargo-ratio${query}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load cargo ratio selections (${response.status})`
      );
    }

    const result = await response.json();
    return unwrapListItems(result.data ?? result);
  };

  if (!fks.length) {
    return fetchByQuery('');
  }

  const lists = await Promise.all(
    fks.map(fk =>
      fetchByQuery(`?QuantityInformationFK=${encodeURIComponent(fk)}`).catch(
        () => []
      )
    )
  );

  const byId = new Map();
  lists.flat().forEach(item => {
    const id = pick(item, 'id', 'Id');
    if (id === null || id === undefined || id === '') return;
    byId.set(String(id), item);
  });

  return byId.size ? [...byId.values()] : lists.flat();
};

const bulkUpsertCargoRatioSelections = async ({
  quantityInformationFk,
  quantityIncotermFk,
  items,
  token,
}) => {
  const response = await fetch(
    `${api.API_URL}/quantity-information-cargo-ratio/bulk-upsert`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        quantityInformationFK: quantityInformationFk,
        quantityIncotermFK: quantityIncotermFk,
        items,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to bulk upsert cargo ratio selections (${response.status})`
    );
  }

  const result = await response.json();
  return result.data ?? result;
};

const deleteCargoRatioSelection = async (selectionId, token) => {
  const response = await fetch(
    `${api.API_URL}/quantity-information-cargo-ratio/${selectionId}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to remove cargo ratio selection (${response.status})`
    );
  }
};

const postCargoRatioSelection = async ({
  quantityInformationFk,
  quantityIncotermFk,
  cargoRatioId,
  parcelSize = null,
  alpha = null,
  token,
}) => {
  const response = await fetch(
    `${api.API_URL}/quantity-information-cargo-ratio`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        quantityInformationFK: quantityInformationFk,
        quantityIncotermFK: quantityIncotermFk,
        cargoRatio: resolveCargoRatioValue(cargoRatioId),
        parcelSize:
          parcelSize === '' || parcelSize === undefined ? null : parcelSize,
        alpha: alpha === '' || alpha === undefined ? null : alpha,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Failed to save cargo ratio selection (${response.status})`
    );
  }

  const result = await response.json();
  return result.data ?? result;
};

const sortSelectedRatios = (ratios = []) =>
  [...ratios].sort(
    (a, b) =>
      Number(resolveCargoRatioValue(a.id)) -
      Number(resolveCargoRatioValue(b.id))
  );

const isFilled = value =>
  value !== null && value !== undefined && String(value).trim() !== '';

const formatAldAddSchedule = (timelineType, startDate, endDate) => {
  const dateRange = [startDate, endDate].filter(isFilled).join(' - ');
  const schedule = [timelineType, dateRange].filter(isFilled).join(' ');
  return schedule || '-';
};

export const getFobLocationNames = data =>
  (Array.isArray(data?.incotermFOBLocations) ? data.incotermFOBLocations : [])
    .map(location => location?.name ?? location?.Name ?? location)
    .filter(isFilled);

const MAX_MULTIPORT_PORTS = 3;

const mapPortEntry = ({ id, port, timelineType, startDate, endDate }) => ({
  id,
  port: port || '-',
  aldAdd: formatAldAddSchedule(timelineType, startDate, endDate),
});

const mapIncotermRowBase = ({
  id,
  quantityIncotermId,
  timelineType,
  startDate,
  endDate,
  incoterm,
  route,
  incotermPortType,
  ports,
}) => {
  const resolvedPorts = (ports ?? []).slice(0, MAX_MULTIPORT_PORTS);
  const defaultAldAdd = formatAldAddSchedule(timelineType, startDate, endDate);
  const slot = index => resolvedPorts[index] ?? null;
  const port1 = slot(0);
  const port2 = slot(1);
  const port3 = slot(2);

  return {
    id,
    quantityIncotermId: quantityIncotermId ?? null,
    aldAdd: defaultAldAdd,
    incotermRoute:
      [incoterm, route, incotermPortType].filter(isFilled).join(' - ') || '-',
    route: route ?? null,
    incotermPortType: incotermPortType ?? null,
    port1: port1?.port ?? '-',
    port1Names: port1?.portNames ?? null,
    port1AldAdd: port1?.aldAdd ?? defaultAldAdd,
    port2: port2?.port ?? null,
    port2Names: port2?.portNames ?? null,
    port2AldAdd: port2?.aldAdd ?? null,
    port3: port3?.port ?? null,
    port3Names: port3?.portNames ?? null,
    port3AldAdd: port3?.aldAdd ?? null,
    port: port1?.port ?? '-',
    ports: resolvedPorts.length
      ? resolvedPorts
      : [
          {
            id: `${id}-port-0`,
            port: '-',
            aldAdd: defaultAldAdd,
          },
        ],
    alpha: 'Requested',
  };
};

const collectMultiPortEntries = (data, index) => {
  const primaryAldAdd = formatAldAddSchedule(
    data.timelineType,
    data.startDate,
    data.endDate
  );

  const candidates = [
    {
      id: `primary-${index}`,
      port: data.incotermLocation,
      timelineType: data.timelineType,
      startDate: data.startDate,
      endDate: data.endDate,
    },
    {
      id: `secondary-${index}`,
      port: data.incotermLocation2,
      timelineType: data.timelineType,
      startDate: isFilled(data.startDate2) ? data.startDate2 : data.startDate,
      endDate: isFilled(data.endDate2) ? data.endDate2 : data.endDate,
    },
    {
      id: `tertiary-${index}`,
      port: data.incotermLocation3,
      timelineType: data.timelineType,
      startDate: isFilled(data.startDate3) ? data.startDate3 : data.startDate,
      endDate: isFilled(data.endDate3) ? data.endDate3 : data.endDate,
    },
  ];

  return candidates
    .filter(candidate => isFilled(candidate.port))
    .slice(0, MAX_MULTIPORT_PORTS)
    .map(candidate => {
      const entry = mapPortEntry(candidate);
      return {
        ...entry,
        aldAdd: entry.aldAdd !== '-' ? entry.aldAdd : primaryAldAdd,
      };
    });
};

export const getIncotermBasesFromParcel = parcel => {
  const timelineItems = parcel?.incotermTimelineItems ?? [];

  if (timelineItems.length) {
    return timelineItems.flatMap((data, index) => {
      const fobLocationNames = getFobLocationNames(data);

      if (fobLocationNames.length) {
        const aldAdd = formatAldAddSchedule(
          data.timelineType,
          data.startDate,
          data.endDate
        );
        const ports = [
          {
            id: `fob-${index}-port-0`,
            port: fobLocationNames.join(', '),
            portNames: fobLocationNames,
            aldAdd,
          },
        ];

        if (isFilled(data.incotermLocation2)) {
          ports.push({
            id: `fob-${index}-port-1`,
            port: data.incotermLocation2,
            portNames: [data.incotermLocation2],
            aldAdd: formatAldAddSchedule(
              data.timelineType,
              isFilled(data.startDate2) ? data.startDate2 : data.startDate,
              isFilled(data.endDate2) ? data.endDate2 : data.endDate
            ),
          });
        }

        return [
          mapIncotermRowBase({
            id: `fob-${index}`,
            quantityIncotermId: data.quantityIncotermId,
            timelineType: data.timelineType,
            startDate: data.startDate,
            endDate: data.endDate,
            incoterm: data.incoterm,
            route: data.incotermRoute,
            incotermPortType: data.incotermPortType,
            ports,
          }),
        ];
      }

      if (data.incotermPortType === 'MultiPort') {
        const ports = collectMultiPortEntries(data, index);
        if (!ports.length) return [];

        return [
          mapIncotermRowBase({
            id: `multiport-${index}`,
            quantityIncotermId: data.quantityIncotermId,
            timelineType: data.timelineType,
            startDate: data.startDate,
            endDate: data.endDate,
            incoterm: data.incoterm,
            route: data.incotermRoute,
            incotermPortType: data.incotermPortType,
            ports,
          }),
        ];
      }

      if (!isFilled(data.incotermLocation)) return [];

      const aldAdd = formatAldAddSchedule(
        data.timelineType,
        data.startDate,
        data.endDate
      );

      return [
        mapIncotermRowBase({
          id: `primary-${index}`,
          quantityIncotermId: data.quantityIncotermId,
          timelineType: data.timelineType,
          startDate: data.startDate,
          endDate: data.endDate,
          incoterm: data.incoterm,
          route: data.incotermRoute,
          incotermPortType: data.incotermPortType,
          ports: [
            {
              id: `primary-${index}-port-0`,
              port: data.incotermLocation,
              aldAdd,
            },
          ],
        }),
      ];
    });
  }

  return (parcel?.incotermRows ?? []).map((row, index) => {
    const aldAdd = row.aldAdd2 || row.aldAdd1 || '-';
    const port1 = row.port1 || row.loadingPort || '-';
    const port2 = row.port2 || null;
    const port3 = row.port3 || null;

    return {
      id: `fallback-${index}`,
      quantityIncotermId: row.quantityIncotermId ?? null,
      aldAdd,
      incotermRoute:
        [row.incoterm, row.route, row.incotermPortType]
          .filter(isFilled)
          .join(' - ') || '-',
      route: row.route ?? null,
      incotermPortType: row.incotermPortType ?? null,
      port1,
      port1Names: null,
      port1AldAdd: aldAdd,
      port2,
      port2Names: null,
      port2AldAdd: row.aldAdd2 || null,
      port3,
      port3Names: null,
      port3AldAdd: row.aldAdd3 || null,
      port: port1,
      ports: [
        { id: `fallback-${index}-port-0`, port: port1, aldAdd },
        ...(isFilled(port2)
          ? [{ id: `fallback-${index}-port-1`, port: port2, aldAdd }]
          : []),
        ...(isFilled(port3)
          ? [{ id: `fallback-${index}-port-2`, port: port3, aldAdd }]
          : []),
      ],
      alpha: 'Requested',
    };
  });
};

export const getIncotermBaseFromParcel = parcel =>
  getIncotermBasesFromParcel(parcel)[0] ?? {};

export const buildLpgIncotermTableModel = ({
  incotermBases = [],
  parcelSizes = [],
  quantityTargets = [],
  selections = [],
  confirmedRatios = [],
  unitLabel = 'MT',
  incotermAlphaMap = new Map(),
}) => {
  const resolvedParcelSizes =
    parcelSizes.length > 0
      ? parcelSizes
      : formatParcelSizes(quantityTargets, unitLabel);

  if (!incotermBases.length || !resolvedParcelSizes.length) {
    return { rows: [], hasCargoRatioSelection: false };
  }

  const hasCargoRatioSelection = confirmedRatios.length > 0;
  const quantityByFk = new Map(
    quantityTargets.map(target => [
      String(target.quantityInformationFk),
      target,
    ])
  );

  const alphaHpsMap = new Map();
  quantityTargets.forEach(target => {
    (target.cargoRatios || []).forEach(cr => {
      if (cr.cargoRatio !== null && cr.cargoRatio !== undefined) {
        alphaHpsMap.set(
          `${String(target.quantityInformationFk)}-${getCargoRatioKey(cr.cargoRatio)}`,
          cr.alphaHps
        );
      }
    });
  });

  const resolveAlphaHps = (quantityInformationFk, cargoRatio) => {
    if (
      quantityInformationFk === null ||
      quantityInformationFk === undefined ||
      cargoRatio === null ||
      cargoRatio === undefined
    ) {
      return null;
    }
    return (
      alphaHpsMap.get(
        `${String(quantityInformationFk)}-${getCargoRatioKey(cargoRatio)}`
      ) ?? null
    );
  };

  const resolveIncotermAlpha = (
    incotermId,
    quantityInformationFk,
    cargoRatio
  ) => {
    if (
      !incotermAlphaMap.size ||
      incotermId === null ||
      incotermId === undefined ||
      quantityInformationFk === null ||
      quantityInformationFk === undefined ||
      cargoRatio === null ||
      cargoRatio === undefined
    ) {
      return null;
    }
    return (
      incotermAlphaMap.get(
        `${String(incotermId)}-${String(quantityInformationFk)}-${getCargoRatioKey(cargoRatio)}`
      ) ?? null
    );
  };

  const resolveParcelLabel = selection => {
    const fromSelection = pick(selection, 'parcelSize', 'ParcelSize');
    if (
      fromSelection !== null &&
      fromSelection !== undefined &&
      String(fromSelection).trim() !== ''
    ) {
      return `${formatNumber(fromSelection)} ${unitLabel}`;
    }
    const qiFk = pick(
      selection,
      'quantityInformationFK',
      'QuantityInformationFK'
    );
    const target = quantityByFk.get(String(qiFk ?? ''));
    if (target?.quantity !== null && target?.quantity !== undefined) {
      return `${formatNumber(target.quantity)} ${unitLabel}`;
    }
    return '-';
  };

  const getParcelSortValue = selection => {
    const fromSelection = Number(pick(selection, 'parcelSize', 'ParcelSize'));
    if (Number.isFinite(fromSelection)) return fromSelection;
    const qiFk = pick(
      selection,
      'quantityInformationFK',
      'QuantityInformationFK'
    );
    const quantity = Number(quantityByFk.get(String(qiFk ?? ''))?.quantity);
    return Number.isFinite(quantity) ? quantity : 0;
  };

  const buildRatioParcelEntries = incotermId => {
    if (!hasCargoRatioSelection) {
      return resolvedParcelSizes.map((parcelSize, sizeIndex) => ({
        parcelSize,
        cargoRatioLabel: null,
        ratioKey: `pending-${sizeIndex}`,
        sizeIndex,
        ratioIndex: 0,
        alphaHps: null,
      }));
    }

    const scoped = selections.filter(selection => {
      const fk = getSelectionIncotermFk(selection);
      if (!incotermId) return true;
      if (fk === null || fk === undefined || fk === '') return true;
      return String(fk) === String(incotermId);
    });

    const entries = [];
    confirmedRatios.forEach((ratio, ratioIndex) => {
      const ratioKey = getCargoRatioKey(ratio.id);
      const matched = scoped
        .filter(
          selection =>
            getCargoRatioKey(pick(selection, 'cargoRatio', 'CargoRatio')) ===
            ratioKey
        )
        .sort((a, b) => getParcelSortValue(a) - getParcelSortValue(b));

      if (matched.length) {
        matched.forEach((selection, sizeIndex) => {
          const qiFk = pick(
            selection,
            'quantityInformationFK',
            'QuantityInformationFK'
          );
          const cr = pick(selection, 'cargoRatio', 'CargoRatio');
          entries.push({
            parcelSize: resolveParcelLabel(selection),
            cargoRatioLabel: ratio.label,
            ratioKey,
            sizeIndex,
            ratioIndex,
            alphaHps:
              resolveIncotermAlpha(incotermId, qiFk, cr) ??
              resolveAlphaHps(qiFk, cr),
          });
        });
        return;
      }

      quantityTargets
        .slice()
        .sort((a, b) => Number(a.quantity) - Number(b.quantity))
        .forEach((target, sizeIndex) => {
          entries.push({
            parcelSize: `${formatNumber(target.quantity)} ${unitLabel}`,
            cargoRatioLabel: ratio.label,
            ratioKey,
            sizeIndex,
            ratioIndex,
            alphaHps:
              resolveIncotermAlpha(
                incotermId,
                target.quantityInformationFk,
                ratio.id
              ) ?? null,
          });
        });
    });

    return entries;
  };

  const rows = [];

  incotermBases.forEach(base => {
    const ratioParcelEntries = buildRatioParcelEntries(base.quantityIncotermId);
    const groupRowSpan =
      ratioParcelEntries.length || resolvedParcelSizes.length;
    const groupParcelSizes = [
      ...new Set(
        ratioParcelEntries
          .map(entry => entry.parcelSize)
          .filter(label => isFilled(label) && label !== '-')
      ),
    ];

    ratioParcelEntries.forEach((entry, entryIndex) => {
      const isFirstInIncoterm = entryIndex === 0;
      const isFirstInRatio = entry.sizeIndex === 0;
      const ratioRowSpan = ratioParcelEntries.filter(
        item => item.ratioKey === entry.ratioKey
      ).length;

      rows.push({
        id: `${base.id}-${entry.ratioKey}-${entry.sizeIndex}`,
        incotermRoute: base.incotermRoute || base.incoterm || '-',
        route: base.route ?? null,
        port1: base.port1 ?? base.port ?? '-',
        port1Names: base.port1Names ?? null,
        port1AldAdd: base.port1AldAdd ?? base.aldAdd ?? '-',
        port2: base.port2 ?? null,
        port2Names: base.port2Names ?? null,
        port2AldAdd: base.port2AldAdd ?? null,
        port3: base.port3 ?? null,
        port3Names: base.port3Names ?? null,
        port3AldAdd: base.port3AldAdd ?? null,
        groupParcelSizes,
        parcelSize: entry.parcelSize,
        alpha: entry.alphaHps ?? 'Requested',
        showIncotermCells: isFirstInIncoterm,
        incotermRowSpan: groupRowSpan,
        showCargoRatioCell: hasCargoRatioSelection
          ? isFirstInRatio
          : isFirstInIncoterm,
        cargoRatioRowSpan: hasCargoRatioSelection ? ratioRowSpan : groupRowSpan,
        cargoRatioLabel: entry.cargoRatioLabel,
      });
    });
  });

  return {
    rows,
    hasCargoRatioSelection,
  };
};

export const formatParcelSizes = (quantityTargets = [], unitLabel = 'MT') =>
  quantityTargets.map(
    target => `${formatNumber(target.quantity)} ${unitLabel}`
  );

export const filterSelectionsForParcel = (selections, quantityFks) => {
  const fkSet = new Set(quantityFks.map(String));
  return selections.filter(selection =>
    fkSet.has(
      String(pick(selection, 'quantityInformationFK', 'QuantityInformationFK'))
    )
  );
};

export const getConfirmedRatiosFromSelections = (selections, ratioOptions) => {
  const optionByValue = new Map(
    ratioOptions.map(option => [getCargoRatioKey(option.id), option])
  );

  const unique = new Map();

  selections.forEach(selection => {
    const cargoRatio = pick(selection, 'cargoRatio', 'CargoRatio');
    const cargoRatioLabel =
      pick(selection, 'cargoRatioLabel', 'CargoRatioLabel') ??
      optionByValue.get(getCargoRatioKey(cargoRatio))?.label;

    if (cargoRatio === null || cargoRatio === undefined) return;

    const key = getCargoRatioKey(cargoRatio);
    if (!unique.has(key)) {
      unique.set(key, {
        id: optionByValue.get(key)?.id ?? cargoRatio,
        label: cargoRatioLabel ?? key,
        sortOrder: Number(cargoRatio),
      });
    }
  });

  return [...unique.values()].sort(
    (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
  );
};
const syncCargoRatioSelectionsViaDeletePost = async ({
  quantityTargets,
  quantityFks,
  incotermIds = [],
  parcelSelections,
  selectedRatios,
  token,
}) => {
  const selectedRatioValues = new Set(
    selectedRatios.map(ratio => getCargoRatioKey(ratio.id))
  );

  const syncedIncotermIds = new Set(incotermIds.map(String));
  const selectionsToDelete = parcelSelections.filter(selection => {
    const cargoRatio = getCargoRatioKey(
      pick(selection, 'cargoRatio', 'CargoRatio')
    );
    const incotermFk = getSelectionIncotermFk(selection);
    const isLegacyRow =
      incotermFk === null ||
      incotermFk === undefined ||
      incotermFk === '' ||
      !syncedIncotermIds.has(String(incotermFk));

    return !selectedRatioValues.has(cargoRatio) || isLegacyRow;
  });

  if (selectionsToDelete.length) {
    await Promise.all(
      selectionsToDelete.map(selection =>
        deleteCargoRatioSelection(pick(selection, 'id', 'Id'), token)
      )
    );
  }

  const remainingSelections = filterSelectionsForParcel(
    selectionsToDelete.length
      ? await fetchAllCargoRatioSelections(quantityFks)
      : parcelSelections,
    quantityFks
  );

  const existingKeys = new Set(
    remainingSelections.map(selection =>
      getSelectionKey(
        pick(selection, 'quantityInformationFK', 'QuantityInformationFK'),
        getSelectionIncotermFk(selection),
        pick(selection, 'cargoRatio', 'CargoRatio')
      )
    )
  );

  const posts = [];

  selectedRatios.forEach(ratio => {
    quantityTargets.forEach(target => {
      incotermIds.forEach(incotermId => {
        const key = getSelectionKey(
          target.quantityInformationFk,
          incotermId,
          resolveCargoRatioValue(ratio.id)
        );

        if (!existingKeys.has(key)) {
          posts.push(
            postCargoRatioSelection({
              quantityInformationFk: target.quantityInformationFk,
              quantityIncotermFk: incotermId,
              cargoRatioId: ratio.id,
              parcelSize: target.quantity ?? null,
              alpha: null,
              token,
            })
          );
        }
      });
    });
  });

  if (posts.length) {
    await Promise.all(posts);
  }
};

const syncCargoRatioSelectionsViaBulkUpsert = async ({
  quantityTargets,
  incotermIds = [],
  parcelSelections,
  selectedRatios,
  token,
}) => {
  const selectedRatioValues = new Set(
    selectedRatios.map(ratio => getCargoRatioKey(ratio.id))
  );

  // Dikelompokkan per parcel + incoterm. bulk-upsert melakukan full-sync dalam cakupan itu,
  // jadi memanggilnya per parcel saja akan menghapus baris milik incoterm lain.
  const selectionsByKey = new Map();

  parcelSelections.forEach(selection => {
    const key = `${String(
      pick(selection, 'quantityInformationFK', 'QuantityInformationFK')
    )}-${String(getSelectionIncotermFk(selection) ?? '')}`;
    if (!selectionsByKey.has(key)) selectionsByKey.set(key, []);
    selectionsByKey.get(key).push(selection);
  });

  const pairs = quantityTargets.flatMap(target =>
    incotermIds.map(incotermId => ({ target, incotermId }))
  );

  await Promise.all(
    pairs.map(({ target, incotermId }) => {
      const key = `${String(target.quantityInformationFk)}-${String(incotermId)}`;
      const existingForKey = selectionsByKey.get(key) ?? [];

      const items = selectedRatios.map(ratio => {
        const cargoRatio = resolveCargoRatioValue(ratio.id);
        const existing = existingForKey.find(
          selection =>
            getCargoRatioKey(pick(selection, 'cargoRatio', 'CargoRatio')) ===
            getCargoRatioKey(cargoRatio)
        );

        return {
          id: existing ? pick(existing, 'id', 'Id') : '',
          quantityIncotermFK: incotermId,
          cargoRatio,
          parcelSize:
            pick(existing, 'parcelSize', 'ParcelSize') ??
            target.quantity ??
            null,
          alpha: pick(existing, 'alpha', 'Alpha') ?? null,
        };
      });

      return bulkUpsertCargoRatioSelections({
        quantityInformationFk: target.quantityInformationFk,
        quantityIncotermFk: incotermId,
        items,
        token,
      });
    })
  );

  // Sisa baris yang ratio-nya tidak lagi dipilih, termasuk baris legacy tanpa incoterm yang
  // tidak tersentuh bulk-upsert manapun.
  const syncedIncotermIds = new Set(incotermIds.map(String));
  const selectionsToDelete = parcelSelections.filter(selection => {
    const cargoRatio = getCargoRatioKey(
      pick(selection, 'cargoRatio', 'CargoRatio')
    );
    const incotermFk = getSelectionIncotermFk(selection);
    const isLegacyRow =
      incotermFk === null ||
      incotermFk === undefined ||
      incotermFk === '' ||
      !syncedIncotermIds.has(String(incotermFk));

    return !selectedRatioValues.has(cargoRatio) || isLegacyRow;
  });

  if (selectionsToDelete.length) {
    await Promise.all(
      selectionsToDelete.map(selection =>
        deleteCargoRatioSelection(pick(selection, 'id', 'Id'), token).catch(
          () => null
        )
      )
    );
  }
};

export const syncAndLoadCargoRatioTable = async ({
  procurementId,
  quantityDeliveryTermId,
  selectedRatios = [],
  incotermBases = [],
  unitLabel = 'MT',
}) => {
  const token = getAuthToken();
  const emptyModel = buildLpgIncotermTableModel({
    incotermBases,
    quantityTargets: [],
    selections: [],
    confirmedRatios: [],
    unitLabel,
  });

  if (!token || !procurementId || !quantityDeliveryTermId) return emptyModel;

  const [quantityTargets, incotermAlphaMap] = await Promise.all([
    fetchQuantityInformationTargets(procurementId, quantityDeliveryTermId),
    fetchIncotermAlphaMap(quantityDeliveryTermId),
  ]);

  if (!quantityTargets.length) return emptyModel;

  const quantityFks = quantityTargets.map(
    target => target.quantityInformationFk
  );

  const existingSelections = await fetchAllCargoRatioSelections(quantityFks);
  const parcelSelections = filterSelectionsForParcel(
    existingSelections,
    quantityFks
  );

  const sortedRatios = sortSelectedRatios(selectedRatios);

  // Cargo ratio dipilih di tingkat delivery term, tetapi disimpan per incoterm. Tanpa daftar
  // incoterm tidak ada tujuan penyimpanan yang sah, jadi biarkan data lama apa adanya.
  const incotermIds = collectIncotermIdsFromBases(incotermBases);
  if (!incotermIds.length) {
    return buildLpgIncotermTableModel({
      incotermBases,
      quantityTargets,
      selections: parcelSelections,
      confirmedRatios: sortedRatios,
      unitLabel,
      incotermAlphaMap,
    });
  }

  try {
    await syncCargoRatioSelectionsViaBulkUpsert({
      quantityTargets,
      incotermIds,
      parcelSelections,
      selectedRatios,
      token,
    });
  } catch {
    await syncCargoRatioSelectionsViaDeletePost({
      quantityTargets,
      quantityFks,
      incotermIds,
      parcelSelections,
      selectedRatios,
      token,
    });
  }

  const syncedSelections = filterSelectionsForParcel(
    await fetchAllCargoRatioSelections(quantityFks),
    quantityFks
  );

  return buildLpgIncotermTableModel({
    incotermBases,
    quantityTargets,
    selections: syncedSelections,
    confirmedRatios: sortedRatios,
    unitLabel,
    incotermAlphaMap,
  });
};

export const loadCargoRatioTable = async ({
  procurementId,
  quantityDeliveryTermId,
  incotermBases = [],
  unitLabel = 'MT',
  skipCargoRatio = false,
}) => {
  const [quantityTargets, incotermAlphaMap] = await Promise.all([
    fetchQuantityInformationTargets(procurementId, quantityDeliveryTermId),
    fetchIncotermAlphaMap(quantityDeliveryTermId),
  ]);

  if (!quantityTargets.length) {
    return {
      tableModel: buildLpgIncotermTableModel({
        incotermBases,
        quantityTargets: [],
        selections: [],
        confirmedRatios: [],
        unitLabel,
        incotermAlphaMap,
      }),
      confirmedRatios: [],
      ratioOptions: [],
    };
  }

  if (skipCargoRatio) {
    return {
      tableModel: buildLpgIncotermTableModel({
        incotermBases,
        quantityTargets,
        selections: [],
        confirmedRatios: [],
        unitLabel,
        incotermAlphaMap,
      }),
      confirmedRatios: [],
      ratioOptions: [],
    };
  }

  const quantityFks = quantityTargets.map(
    target => target.quantityInformationFk
  );
  const selections = filterSelectionsForParcel(
    await fetchAllCargoRatioSelections(quantityFks),
    quantityFks
  );

  const ratioOptions = await fetchCargoRatioOptions();
  const confirmedRatios = getConfirmedRatiosFromSelections(
    selections,
    ratioOptions
  );

  return {
    tableModel: buildLpgIncotermTableModel({
      incotermBases,
      quantityTargets,
      selections,
      confirmedRatios,
      unitLabel,
      incotermAlphaMap,
    }),
    confirmedRatios,
    ratioOptions,
  };
};
